import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare, Sparkles, Trophy, Phone, Film, MapPin, Bell, X, ArrowRight,
} from "lucide-react";

/** Onboarding slide deck shown to a user the first time they land in the
 * app after signup/first login. Persists a `yappo-onboarded` flag to
 * localStorage so it doesn't reappear. Dismissible from any slide.
 */
const SLIDES = [
  {
    key: "welcome",
    icon: MessageSquare,
    tint: "#CCFF00",
    title: "Yappo'ya hoş geldin!",
    body: "Sohbetleri sıradan mesajlaşmanın ötesine taşıyan bir uygulama. Ruh haline uyum sağlayan atmosferler, günlük görevler, arkadaşlık seviyeleri ve daha fazlası.",
    hint: "Devam et → tanıtımı gör",
  },
  {
    key: "mood",
    icon: Sparkles,
    tint: "#D4B3FF",
    title: "Sohbet ruhunu hisset",
    body: "Her sohbet ne yazdığına göre bir 'ruh hali' üretir: neşeli, romantik, gergin… Sohbetin arka planı ve akan detaylar buna göre değişir. Header'daki emojiye dokunarak yenileyebilirsin.",
    hint: "Header'da 💬 rozetine bak",
  },
  {
    key: "quests",
    icon: Trophy,
    tint: "#FFF7C2",
    title: "Günün görevi",
    body: "Her gün küçük bir görev alırsın: 'ilk 3 mesaj', 'yeni bir emoji dene', 'sesli mesaj gönder'… Tamamladıkça XP + arkadaşlık serisi büyür, yeni temalar açılır.",
    hint: "Sohbetin üstünde göreceksin",
  },
  {
    key: "calls-reels",
    icon: Phone,
    tint: "#A8E6FF",
    title: "Sesli/görüntülü arama & Reels",
    body: "Header'daki telefon ikonundan tek dokunuşla sesli veya görüntülü arama başlat. Alt menüden Reels'e geç, hızlı bir kamera ile 30 saniyelik videolar paylaş.",
    hint: "📞 · 🎥 · 🎬",
  },
  {
    key: "notifications",
    icon: Bell,
    tint: "#FF9E80",
    title: "Bildirimleri kaçırma",
    body: "Sekme kapalıyken bile mesajlar sana ulaşsın. Ayarlar → Bildirimler → \"Uygulama kapalıyken de bildirim\" toggle'ını açman yeterli.",
    hint: "İpucu: Ayarlar'dan aç",
  },
];

const KEY = "yappo-onboarded-v2";

export function hasSeenOnboarding() {
  try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
}

export function markOnboardingSeen() {
  try { localStorage.setItem(KEY, "1"); } catch {}
}

export default function OnboardingSlider({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  if (!open) return null;

  const s = SLIDES[idx];
  const Icon = s.icon;
  const isLast = idx === SLIDES.length - 1;

  const finish = () => {
    markOnboardingSeen();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      data-testid="onboarding-slider"
      role="dialog"
      aria-labelledby="onboarding-title"
    >
      <div className="w-full max-w-md bg-white border-hard shadow-hard-lg rounded-2xl overflow-hidden relative">
        <button
          onClick={finish}
          data-testid="onboarding-skip"
          className="absolute top-3 right-3 z-10 w-9 h-9 border-hard-2 rounded-lg bg-white flex items-center justify-center press-btn shadow-hard-sm"
          aria-label="Atla ve kapat"
        >
          <X strokeWidth={3} className="w-4 h-4" />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            key={s.key}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            className="p-6 md:p-8"
          >
            <div
              className="w-16 h-16 rounded-2xl border-hard shadow-hard flex items-center justify-center mb-5"
              style={{ background: s.tint }}
            >
              <Icon strokeWidth={2.5} className="w-8 h-8" />
            </div>
            <h2 id="onboarding-title" className="font-display text-3xl md:text-4xl leading-tight mb-3">
              {s.title}
            </h2>
            <p className="text-sm md:text-base text-[#333] leading-relaxed">{s.body}</p>
            <p className="mt-4 text-xs uppercase font-black tracking-widest text-[#555]">
              {s.hint}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="px-6 md:px-8 pb-6 md:pb-8 flex items-center gap-3">
          <div className="flex-1 flex items-center gap-1.5" data-testid="onboarding-dots">
            {SLIDES.map((slide, i) => (
              <span
                key={slide.key}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[#0A0A0A]" : "w-2 bg-[#0A0A0A]/25"}`}
              />
            ))}
          </div>
          {!isLast && (
            <button
              onClick={() => setIdx(idx + 1)}
              data-testid="onboarding-next"
              className="h-11 px-4 border-hard rounded-xl bg-[#CCFF00] font-black text-sm press-btn shadow-hard-sm flex items-center gap-2"
            >
              İleri <ArrowRight strokeWidth={3} className="w-4 h-4" />
            </button>
          )}
          {isLast && (
            <button
              onClick={finish}
              data-testid="onboarding-finish"
              className="h-11 px-5 border-hard rounded-xl bg-[#0A0A0A] text-white font-black text-sm press-btn shadow-hard"
            >
              Başlayalım
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
